import React from 'react';

export function QuickActions({ 
  assets = {}, 
  onQuickAction,
  busy = false 
}) {
  const hasAudio = assets.audio?.length > 0;
  const hasImage = assets.images?.length > 0;
  const hasVideo = assets.videos?.length > 0;

  const actions = [
    {
      id: 'voice-first',
      label: '🎵 Voice',
      description: 'Create a voiceover',
      requires: [],
      action: { type: 'audio' }
    },
    {
      id: 'image-first',
      label: '🖼️ Image',
      description: 'Generate a visual',
      requires: [],
      action: { type: 'image' }
    },
    {
      id: 'video-complete',
      label: '🎬 Video',
      description: 'Needs voice + image',
      requires: ['audio', 'image'],
      action: { type: 'video' },
      disabled: !hasAudio || !hasImage
    },
    {
      id: 'batch-audio',
      label: '⚡ Batch Voice',
      description: 'Multi-take voice',
      requires: [],
      action: { type: 'batch-audio' }
    },
    {
      id: 'optimize-existing',
      label: '🔧 Optimize',
      description: 'Improve assets',
      requires: ['audio', 'image'],
      action: { type: 'optimize' },
      disabled: !hasAudio && !hasImage
    }
  ];

  const handleAction = (action) => {
    if (busy) return;
    onQuickAction(action);
  };

  return (
    <div style={{
      background: '#111827',
      border: '1px solid #1f2937',
      borderRadius: 12,
      padding: 16
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ color: '#f9fafb', margin: 0, fontSize: 16 }}>
          Quick Actions
        </h3>
        <div style={{ color: '#9ca3af', fontSize: 12 }}>Tap to generate</div>
      </div>
      
      <div style={{ display: 'grid', gap: 8 }}>
        {actions.map(action => {
          const isDisabled = action.disabled || busy;
          const canExecute = action.requires.every(req => {
            if (req === 'audio') return hasAudio;
            if (req === 'image') return hasImage;
            if (req === 'video') return hasVideo;
            return true;
          });
          
          return (
            <button
              key={action.id}
              onClick={() => handleAction(action.action)}
              disabled={isDisabled || !canExecute}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                background: isDisabled ? '#1f2937' : '#0f172a',
                color: '#e5e7eb',
                border: '1px solid #1f2937',
                borderRadius: 10,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                textAlign: 'left'
              }}
            >
              <div style={{ fontSize: 18 }}>{action.label}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                  {action.description}
                </div>
              </div>
              
              {!canExecute && action.requires.length > 0 && (
                <div style={{
                  fontSize: 11,
                  color: '#fca5a5',
                  textAlign: 'right'
                }}>
                  Needs: {action.requires.filter(r => !assets[r]?.length).join(', ')}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function WorkflowWizard({ 
  currentStep = 1, 
  onStepClick,
  completedSteps = [] 
}) {
  const steps = [
    { id: 1, label: 'Setup', description: 'Context' },
    { id: 2, label: 'Voice', description: 'Audio' },
    { id: 3, label: 'Image', description: 'Visual' },
    { id: 4, label: 'Video', description: 'Combine' }
  ];

  return (
    <div style={{
      background: '#111827',
      border: '1px solid #1f2937',
      borderRadius: 12,
      padding: 16
    }}>
      <h3 style={{ color: '#f9fafb', margin: '0 0 12px 0', fontSize: 16 }}>
        Workflow
      </h3>
      
      <div style={{ display: 'flex', gap: 8 }}>
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = step.id === currentStep;
          const isClickable = step.id <= Math.max(...completedSteps, 1) + 1;
          
          return (
            <div key={step.id} style={{ flex: 1 }}>
              <button
                onClick={() => isClickable && onStepClick?.(step.id)}
                style={{
                  width: '100%',
                  padding: '12px 8px',
                  background: isCompleted ? '#10b981' : isCurrent ? '#ff5a1f' : '#374151',
                  color: '#fff',
                  border: 0,
                  borderRadius: 8,
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  Step {step.id}
                </div>
                <div style={{ fontSize: 10, marginTop: 2 }}>
                  {step.label}
                </div>
              </button>
              
              <div style={{
                fontSize: 10,
                color: '#9ca3af',
                textAlign: 'center',
                marginTop: 4
              }}>
                {step.description}
              </div>
              
              {index < steps.length - 1 && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  right: -4,
                  width: 8,
                  height: 2,
                  background: '#374151',
                  transform: 'translateY(-50%)'
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
